/**
 * Owns the "follow the selection, unless locked" policy for the node-graph view.
 *
 * Domain-agnostic and generic over what a selection resolves to: the mapping is
 * injected as a {@link SelectionResolver}. It's often used to resolve the *cheap*
 * intermediate (e.g. the materials of a selection) and let the host build the
 * heavier representation lazily — so this has no three.js or UI coupling and is
 * unit-testable on its own. A host feeds it the current selection via
 * {@link setTarget} and reacts to {@link onDidChange} (reading {@link items}).
 */

import { Emitter, type Event } from "../../base/common/events.js";
import type { NodeGraph } from "./model.js";

/** Maps a selection target to the resolved items for it (domain-specific). */
export type SelectionResolver<T> = (target: unknown) => T[];

/** @deprecated alias kept for the common node-graph case. Prefer {@link SelectionResolver}. */
export type GraphResolver = SelectionResolver<NodeGraph>;

export class NodeGraphSelectionController<T = NodeGraph> {
    private _locked = false;
    private _latestTarget: unknown = null;
    private _items: T[] | null = null;

    private readonly _onDidChange = new Emitter<T[] | null>();
    /** Fires the resolved items (or `null` when none) when the result changes. */
    readonly onDidChange: Event<T[] | null> = this._onDidChange.event;

    constructor(private readonly resolve: SelectionResolver<T>) {}

    get locked(): boolean { return this._locked; }
    get items(): T[] | null { return this._items; }

    /** Feed the current selection target; refreshes the items unless locked. */
    setTarget(target: unknown): void {
        this._latestTarget = target;
        if (!this._locked) this._update(target);
    }

    /** Lock (freeze current items) or unlock (resync to the latest target). */
    setLocked(locked: boolean): void {
        if (locked === this._locked) return;
        this._locked = locked;
        if (!locked) this._update(this._latestTarget);
    }

    toggleLock(): void {
        this.setLocked(!this._locked);
    }

    private _update(target: unknown): void {
        const resolved = this.resolve(target);
        this._items = resolved.length ? resolved : null;
        this._onDidChange.fire(this._items);
    }

    dispose(): void {
        this._onDidChange.dispose();
    }
}
